import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { BulkScheduleConfirmStatus, BulkSchedulePreviewResponse, MeetingReservationInfo } from "../models";

export const bulkScheduleKeys = {
  all: ["bulkSchedule"] as const,
  preview: (journeyId: string) => [...bulkScheduleKeys.all, "preview", journeyId] as const,
  confirmStatus: (journeyId: string) => [...bulkScheduleKeys.all, "confirmStatus", journeyId] as const,
};

export function useBulkSchedulePreview(journeyId: string, timezone: string) {
  return useQuery<BulkSchedulePreviewResponse>({
    queryKey: bulkScheduleKeys.preview(journeyId),
    queryFn: () =>
      apiClient.get(
        `${generatePath(API_ROUTES.newHireJourneys.bulkSchedule.preview, { id: journeyId })}?timezone=${encodeURIComponent(timezone)}`,
      ),
    staleTime: 0,
    enabled: false,
  });
}

export function useBulkScheduleConfirm(journeyId: string) {
  return useMutation<void, Error, number[]>({
    mutationFn: (reservationIds) =>
      apiClient.post(generatePath(API_ROUTES.newHireJourneys.bulkSchedule.confirm, { id: journeyId }), {
        reservationIds,
      }),
  });
}

export function useBulkScheduleConfirmStatus(journeyId: string, enabled: boolean) {
  return useQuery<BulkScheduleConfirmStatus>({
    queryKey: bulkScheduleKeys.confirmStatus(journeyId),
    queryFn: () =>
      apiClient.get(generatePath(API_ROUTES.newHireJourneys.bulkSchedule.confirmStatus, { id: journeyId })),
    enabled,
    refetchInterval: (query) => {
      if (query.state.data?.status === "completed") return false;
      return 3000;
    },
    staleTime: 0,
  });
}

export function usePrepareBulkSchedule(journeyId: string) {
  return useMutation<void, Error>({
    mutationFn: () =>
      apiClient.post(generatePath(API_ROUTES.newHireJourneys.bulkSchedule.prepare, { id: journeyId }), {}),
  });
}

export function useSkipMeeting(journeyId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<{ success: boolean; id: number; status: string }, Error, number>({
    mutationFn: (meetingId) =>
      apiClient.post(`${generatePath(API_ROUTES.meetings.show, { id: String(meetingId) })}/skip`, {}),
    onSuccess: (res, meetingId) => {
      queryClient.setQueryData<BulkSchedulePreviewResponse>(bulkScheduleKeys.preview(journeyId), (old) => {
        if (!old) return old;
        return {
          ...old,
          meetings: old.meetings.map((m) => (m.meetingId === meetingId ? { ...m, status: res.status } : m)),
        };
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to skip meeting. Please try again.",
        status: "error",
      });
    },
  });
}

export function useUpdateMeetingReservation() {
  return useMutation<
    MeetingReservationInfo,
    Error,
    { id: number; startTime: string; endTime: string; calendarEventId?: string }
  >({
    mutationFn: ({ id, startTime, endTime, calendarEventId }) =>
      apiClient.put(generatePath(API_ROUTES.meetingReservations.show, { id: String(id) }), {
        startTime,
        endTime,
        calendarEventId,
      }),
  });
}
